import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAdminAuth } from '../../context/AdminAuthContext';
import AdminLayout from './AdminLayout';
import { Megaphone, Save, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLOR_PRESETS = [
  { label: 'Orange', value: '#e8690c' },
  { label: 'Blue', value: '#1565c0' },
  { label: 'Green', value: '#2e7d32' },
  { label: 'Red', value: '#c62828' },
  { label: 'Dark', value: '#0f2b4a' },
  { label: 'Purple', value: '#6a1b9a' },
];

const DEFAULT = {
  enabled: false,
  message: '🎉 Limited Time Offer — Get 20% off all courses!',
  bg_color: '#e8690c',
  text_color: '#ffffff',
  link: '/courses',
};

export default function AdminBanner() {
  const { authHeaders } = useAdminAuth();
  const [form, setForm] = useState(DEFAULT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/banner`)
      .then(res => setForm({ ...DEFAULT, ...res.data }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const set = (key, value) => setForm(f => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    setResult(null);
    try {
      await axios.put(`${API}/admin/settings/banner`, form, { headers: authHeaders });
      setResult({ ok: true, msg: 'Banner settings saved.' });
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 size={24} className="animate-spin text-blue-500" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-2xl space-y-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Megaphone size={20} className="text-orange-500" /> Sale Banner
          </h1>
          <p className="text-gray-500 text-sm mt-0.5">Shown site-wide above the navigation. Dismissible per session.</p>
        </div>

        {/* Preview */}
        {form.enabled && (
          <div
            className="rounded-lg px-4 py-3 text-sm font-semibold text-center"
            style={{ backgroundColor: form.bg_color, color: form.text_color }}
          >
            {form.message || 'Your banner message…'}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
          {/* Enable toggle */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800">Enable banner</p>
              <p className="text-xs text-gray-500 mt-0.5">Toggle the banner on or off across the site</p>
            </div>
            <button
              onClick={() => set('enabled', !form.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                form.enabled ? 'bg-blue-600' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  form.enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Banner message</label>
            <input
              type="text"
              value={form.message}
              onChange={e => set('message', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Your sale message…"
            />
          </div>

          {/* Link */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-1">Link (optional)</label>
            <input
              type="text"
              value={form.link}
              onChange={e => set('link', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="/courses"
            />
            <p className="text-xs text-gray-400 mt-1">Clicking the banner goes here. Leave blank to disable the link.</p>
          </div>

          {/* Background colour presets */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Background colour</label>
            <div className="flex flex-wrap gap-2">
              {COLOR_PRESETS.map(c => (
                <button
                  key={c.value}
                  title={c.label}
                  onClick={() => set('bg_color', c.value)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.bg_color === c.value ? 'border-gray-700 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: c.value }}
                />
              ))}
              <div className="flex items-center gap-1.5">
                <label className="text-xs text-gray-500">Custom:</label>
                <input
                  type="color"
                  value={form.bg_color}
                  onChange={e => set('bg_color', e.target.value)}
                  className="h-8 w-10 rounded cursor-pointer border border-gray-200"
                />
              </div>
            </div>
          </div>

          {/* Text colour */}
          <div>
            <label className="block text-sm font-semibold text-gray-800 mb-2">Text colour</label>
            <div className="flex items-center gap-3">
              {['#ffffff', '#000000', '#0f2b4a'].map(c => (
                <button
                  key={c}
                  onClick={() => set('text_color', c)}
                  className={`w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 ${
                    form.text_color === c ? 'border-gray-700 scale-110' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
              <input
                type="color"
                value={form.text_color}
                onChange={e => set('text_color', e.target.value)}
                className="h-8 w-10 rounded cursor-pointer border border-gray-200"
              />
            </div>
          </div>
        </div>

        {result && (
          <div className={`flex items-center gap-2 text-sm px-4 py-3 rounded-lg ${
            result.ok ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
          }`}>
            {result.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />}
            {result.msg}
          </div>
        )}

        <button
          onClick={handleSave}
          disabled={saving}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors"
        >
          {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {saving ? 'Saving…' : 'Save Banner Settings'}
        </button>
      </div>
    </AdminLayout>
  );
}
